"use client";

import {
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  XCircle,
} from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { Database } from "supabase/database.types";

import MarkdownRenderer from "@/components/MarkdownRenderer";
import { Subject } from "@/lib/subjects/types";

interface HelpQuizComponentProps {
  questions: Database["public"]["Tables"]["subject_example_questions"]["Row"][];
  subject: Subject;
  title: string;
}

const stripImageLatex = (content: string) => {
  return content.replace(/!\[\]\([^)]+gif\.latex\)/g, "");
};

interface Answer {
  answer: string;
  correct_answer: boolean;
  display_order: number;
}

const QUESTIONS_PER_PAGE = 10;

export default function HelpQuiz({
  questions,
  subject: _subject,
  title,
}: HelpQuizComponentProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Pagination state
  const shouldPaginate = questions.length > QUESTIONS_PER_PAGE;
  const currentPage = shouldPaginate
    ? parseInt(searchParams.get("page") || "1")
    : 1;
  const totalPages = shouldPaginate
    ? Math.ceil(questions.length / QUESTIONS_PER_PAGE)
    : 1;

  // Get questions for current page
  const startIndex = shouldPaginate
    ? (currentPage - 1) * QUESTIONS_PER_PAGE
    : 0;
  const endIndex = shouldPaginate
    ? startIndex + QUESTIONS_PER_PAGE
    : questions.length;
  const currentQuestions = questions.slice(startIndex, endIndex);

  const firstQuestionNumber = startIndex + 1;
  const lastQuestionNumber = Math.min(
    startIndex + currentQuestions.length,
    questions.length
  );

  // Update URL when page changes
  const updatePage = (page: number) => {
    const params = new URLSearchParams(searchParams.toString());
    if (page === 1) {
      params.delete("page");
    } else {
      params.set("page", page.toString());
    }
    router.push(`?${params.toString()}`, { scroll: false });
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      updatePage(currentPage + 1);
      window.scrollTo({ behavior: "smooth", top: 0 });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      updatePage(currentPage - 1);
      window.scrollTo({ behavior: "smooth", top: 0 });
    }
  };

  if (!questions || questions.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-slate-600 dark:text-slate-400">
          No questions available for this help topic.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Help Questions</h1>
            <p className="text-emerald-100 mt-1 capitalize">{title}</p>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold whitespace-nowrap">
              {`Questions ${firstQuestionNumber} - ${lastQuestionNumber}`}
            </div>
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className={`p-8 ${shouldPaginate ? "space-y-8" : "space-y-12"}`}>
        {currentQuestions.map((question, relativeIndex) => {
          const globalQuestionIndex = startIndex + relativeIndex;

          return (
            <div
              className={`${
                shouldPaginate
                  ? "border-b border-slate-200 dark:border-slate-700 pb-6 last:border-b-0 last:pb-0"
                  : "border-b border-slate-200 dark:border-slate-700 pb-8 last:border-b-0 last:pb-0"
              }`}
              key={globalQuestionIndex}
            >
              {/* Question */}
              <div className="mb-8">
                <div className="flex items-start gap-3 mb-6">
                  <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center">
                    <span className="text-emerald-600 dark:text-emerald-400 font-bold text-sm">
                      {globalQuestionIndex + 1}
                    </span>
                  </div>
                  <div className="flex-1 py-1">
                    <MarkdownRenderer
                      className="prose-lg [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
                      content={stripImageLatex(question.question)}
                    />
                  </div>
                </div>

                {/* Answer Options - Now Static */}
                <div className="space-y-3 mb-6">
                  {(question.answers as unknown as Answer[]).map(
                    (answer, answerIndex) => {
                      const isCorrectAnswer = answer.correct_answer;

                      // Always show correct answers as highlighted
                      const buttonStyle = isCorrectAnswer
                        ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                        : "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50";

                      return (
                        <div
                          className={`w-full p-4 text-left rounded-lg border-2 ${buttonStyle} cursor-default`}
                          key={answerIndex}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <div className="w-6 h-6 rounded-full flex items-center justify-center">
                                {isCorrectAnswer ? (
                                  <CheckCircle className="w-5 h-5 text-green-500" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-slate-400" />
                                )}
                              </div>
                            </div>
                            <div className="flex-1 my-0.25">
                              <MarkdownRenderer
                                className="prose-sm text-sm [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:mb-0"
                                content={stripImageLatex(answer.answer)}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>

                {/* Explanation - Always Visible */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 border-2 border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <Lightbulb className="w-6 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                        Explanation
                      </h4>
                      <MarkdownRenderer
                        className="prose-sm text-sm [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 text-blue-800 dark:text-blue-200"
                        content={stripImageLatex(question.explanation)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {shouldPaginate && (
        <div className="p-6 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <button
              className="flex items-center gap-2 px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={currentPage === 1}
              onClick={goToPreviousPage}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>

            <div className="text-sm text-slate-600 dark:text-slate-400">
              Page {currentPage} of {totalPages}
            </div>

            <button
              className="flex items-center gap-2 px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={currentPage === totalPages}
              onClick={goToNextPage}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
